import React, { useCallback, useMemo, useState } from "react";
import { ArrowLeft } from "lucide-react";
import PilotBanner from "@/modules/racket_lab_admin/PilotBanner";
import RLNavSidebar from "@/modules/racket_lab_admin/RLNavSidebar";
import PromoteMembershipForm from "@/modules/racket_lab_admin/PromoteMembershipForm";
import RollbackMembershipForm from "@/modules/racket_lab_admin/RollbackMembershipForm";
import VerifyMembershipForm from "@/modules/racket_lab_admin/VerifyMembershipForm";
import AuditPromotionViewer from "@/modules/racket_lab_admin/AuditPromotionViewer";
import DryRunDiffPanel from "@/modules/racket_lab_admin/DryRunDiffPanel";
import ApplyConfirmStrip from "@/modules/racket_lab_admin/ApplyConfirmStrip";
import JSONInspector from "@/modules/racket_lab_admin/JSONInspector";
import useDryRunApply from "@/hooks/useDryRunApply";
import useAdminApi from "@/hooks/useAdminApi";
import { Link, useLocation } from "react-router-dom";
import { useRacketLabAdminT } from "@/i18n/racketLabAdminI18n";

/**
 * RLAdminOpsPromote — Sub-sprint D-α.
 *
 * Implements the S6 promote dry-run → apply flow using
 * `useDryRunApply` and `ApplyConfirmStrip`. Also surfaces an aggregate
 * snapshot from `/promotion-overview`.
 */
const Stat = ({ label, value, testId }) => (
  <div data-testid={testId} className="flex flex-col items-start rounded-md border border-slate-200 bg-white px-3 py-2">
    <span className="text-[10px] font-medium uppercase tracking-wide text-slate-500">{label}</span>
    <span className="text-sm font-semibold text-slate-800">{value ?? "—"}</span>
  </div>
);

const RLAdminOpsPromote = () => {
  const t = useRacketLabAdminT();
  // RL-SEED-MEMBERSHIPS-HELPER — hydrate state from `location.state.prefillIntent`
  // ONCE via lazy initializer when navigated from the Seed helper page. This
  // does NOT auto-run the dry-run; the operator must click Dry-run explicitly,
  // preserving the T2 friction tier.
  const location = useLocation();
  const prefillSource = location.state?.prefillIntent?.source || null;
  const [intent, setIntent] = useState(() => {
    const pre = location.state?.prefillIntent;
    if (pre) {
      return {
        user_id: pre.user_id || "",
        role: pre.role || "shop_manager",
        organisation_id: pre.organisation_id || "TEAMSOFTWAREINNOVATIONS",
        shop_ids: Array.isArray(pre.shop_ids) ? pre.shop_ids : [],
      };
    }
    return {
      user_id: "",
      role: "shop_manager",
      organisation_id: "TEAMSOFTWAREINNOVATIONS",
      shop_ids: [],
    };
  });

  const buildPayload = useCallback(() => ({
    user_id: intent.user_id?.trim() || "",
    role: intent.role,
    organisation_id: intent.organisation_id || null,
    shop_ids: intent.shop_ids || [],
  }), [intent]);

  const {
    dryRun, applyState, loading, error, stale,
    runDryRun, applyAction, reset,
  } = useDryRunApply({
    endpoint: "/admin/racket-lab/_ops/promote-membership",
    buildPayload,
  });

  const { data: overview, reload: reloadOverview } = useAdminApi(
    "/admin/racket-lab/_ops/promotion-overview",
  );

  const canDryRun = Boolean(intent.user_id?.trim()) && !loading;
  const applyDisabled =
    !dryRun || stale || dryRun?.kill_switch_active === true || loading;
  const disabledReason = useMemo(() => {
    if (!dryRun) return t("ops.common.run_dry_run_first");
    if (stale) return t("ops.common.input_changed_rerun");
    if (dryRun.kill_switch_active === true) return t("ops.common.kill_switch_blocked");
    if (loading) return t("ops.common.in_flight");
    return null;
  }, [dryRun, stale, loading, t]);

  const onApply = async () => {
    const res = await applyAction({});
    if (res) reloadOverview();
  };

  return (
    <div data-testid="rl-admin-ops-promote-page" className="flex flex-col gap-4">
      <PilotBanner />
      <RLNavSidebar />

      <Link
        to="/admin/racket-lab/ops"
        data-testid="rl-ops-promote-back"
        className="inline-flex w-fit items-center gap-1 text-xs text-slate-600 hover:text-slate-900"
      >
        <ArrowLeft className="w-3.5 h-3.5" /> {t("ops.common.back_to_toolkit")}
      </Link>

      <header>
        <h1 className="text-xl font-semibold text-slate-900">{t("ops.promote.title_full")}</h1>
        <p className="text-xs text-slate-500">{t("ops.promote.subtitle")}</p>
      </header>

      {overview && (
        <section data-testid="rl-ops-promotion-overview" className="flex flex-wrap gap-2">
          <Stat label={t("ops.promote.stats.memberships")} value={overview?.totals?.memberships_total} testId="rl-ops-overview-total" />
          <Stat label={t("ops.promote.stats.active")} value={overview?.totals?.active} testId="rl-ops-overview-active" />
          <Stat label={t("ops.promote.stats.revoked")} value={overview?.totals?.revoked} testId="rl-ops-overview-revoked" />
          <Stat label={t("ops.promote.stats.pending_onboarding")} value={overview?.totals?.pending_onboarding} testId="rl-ops-overview-pending" />
          <Stat label={t("ops.promote.stats.audit_recent")} value={overview?.audit_events_count} testId="rl-ops-overview-audit-recent" />
        </section>
      )}

      <section className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <div className="rounded-md border border-slate-200 bg-white p-4">
          <h2 className="mb-2 text-sm font-medium text-slate-800">{t("ops.promote.intent_title")}</h2>
          {prefillSource && (
            <p
              data-testid="rl-ops-promote-prefill-banner"
              className="mb-2 inline-flex rounded-md bg-sky-50 px-2 py-1 text-[10px] font-medium text-sky-800"
            >
              {t("ops.promote.prefill_banner", { source: prefillSource })}
            </p>
          )}
          <PromoteMembershipForm value={intent} onChange={setIntent} disabled={loading} />
          <div className="mt-3 flex flex-wrap items-center gap-2">
            <button
              type="button"
              data-testid="rl-ops-promote-dry-run-btn"
              disabled={!canDryRun}
              onClick={runDryRun}
              className={`rounded-md px-3 py-1.5 text-xs font-medium text-white ${canDryRun ? "bg-sky-600 hover:bg-sky-700" : "bg-sky-300 cursor-not-allowed"}`}
            >
              {t("ops.promote.dry_run_btn")}
            </button>
            <button
              type="button"
              data-testid="rl-ops-promote-reset-btn"
              onClick={reset}
              disabled={loading}
              className="rounded-md border border-slate-300 bg-white px-3 py-1.5 text-xs hover:bg-slate-50"
            >
              {t("ops.promote.reset_btn")}
            </button>
            {loading && <span className="text-[11px] text-slate-500">{t("ops.common.in_flight_ellipsis")}</span>}
          </div>
        </div>

        <div className="flex flex-col gap-3">
          <DryRunDiffPanel dryRun={dryRun} stale={stale} dataTestId="rl-ops-promote-dry-run-result" />
          <ApplyConfirmStrip
            disabled={applyDisabled}
            disabledReason={disabledReason}
            onApply={onApply}
            confirmPhrase="APPLY"
            applyLabel={t("ops.promote.apply")}
            dataTestId="rl-ops-promote-apply-strip"
          />
        </div>
      </section>

      {error && (
        <div data-testid="rl-ops-promote-error" className="rounded-md border border-rose-200 bg-rose-50 p-3 text-xs text-rose-800">
          {t("ops.common.error_prefix")} {error.status || ""}: {error.detail || t("ops.common.error_unknown")}
        </div>
      )}

      {applyState && (
        <section data-testid="rl-ops-promote-apply-result" className="rounded-md border border-emerald-200 bg-emerald-50 p-3">
          <p className="mb-2 text-xs font-medium text-emerald-900">
            {t("ops.promote.applied_line", { action: applyState.action, at: applyState.at })}
          </p>
          <JSONInspector value={applyState} dataTestId="rl-ops-promote-apply-json" />
        </section>
      )}

      {/* Sub-sprint D-β-α — Rollback / Verify / Audit additions. */}
      <hr className="my-2 border-slate-200" />
      <RollbackSection reloadOverview={reloadOverview} />
      <VerifyMembershipForm dataTestId="rl-ops-verify-form" />
      <AuditPromotionViewer dataTestId="rl-ops-audit" />
    </div>
  );
};

/**
 * RollbackSection — Sub-sprint D-β-α.
 *
 * Wraps `useDryRunApply` against `/rollback-membership` with the
 * `RollbackMembershipForm`. Apply requires reason min 3 chars + valid
 * confirmation_token + non-stale + non-kill-switch.
 */
const RollbackSection = ({ reloadOverview }) => {
  const t = useRacketLabAdminT();
  const [intent, setIntent] = useState({ user_id: "", reason: "" });

  const buildPayload = useCallback(() => ({
    user_id: intent.user_id?.trim() || "",
    reason: intent.reason?.trim() || "",
  }), [intent]);

  const {
    dryRun, applyState, loading, error, stale,
    runDryRun, applyAction, reset,
  } = useDryRunApply({
    endpoint: "/admin/racket-lab/_ops/rollback-membership",
    buildPayload,
  });

  const reasonOk = (intent.reason || "").trim().length >= 3;
  const userOk = Boolean(intent.user_id?.trim());
  const canDryRun = userOk && reasonOk && !loading;
  const applyDisabled =
    !dryRun || stale || dryRun?.kill_switch_active === true || !reasonOk || loading;
  const disabledReason = useMemo(() => {
    if (!reasonOk) return t("ops.common.reason_min_3");
    if (!dryRun) return t("ops.common.run_dry_run_first");
    if (stale) return t("ops.common.input_changed_rerun");
    if (dryRun.kill_switch_active === true) return t("ops.common.kill_switch_blocked");
    if (loading) return t("ops.common.in_flight");
    return null;
  }, [dryRun, stale, loading, reasonOk, t]);

  const onApply = async () => {
    const res = await applyAction({});
    if (res) reloadOverview?.();
  };

  return (
    <section data-testid="rl-ops-rollback-section" className="grid grid-cols-1 gap-4 md:grid-cols-2">
      <div className="rounded-md border border-slate-200 bg-white p-4">
        <h2 className="mb-2 text-sm font-medium text-slate-800">{t("ops.rollback.section_title")}</h2>
        <RollbackMembershipForm value={intent} onChange={setIntent} disabled={loading} />
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <button
            type="button"
            data-testid="rl-ops-rollback-dry-run-btn"
            disabled={!canDryRun}
            onClick={runDryRun}
            className={`rounded-md px-3 py-1.5 text-xs font-medium text-white ${canDryRun ? "bg-rose-600 hover:bg-rose-700" : "bg-rose-300 cursor-not-allowed"}`}
          >
            {t("ops.rollback.dry_run_btn")}
          </button>
          <button
            type="button"
            data-testid="rl-ops-rollback-reset-btn"
            onClick={reset}
            disabled={loading}
            className="rounded-md border border-slate-300 bg-white px-3 py-1.5 text-xs hover:bg-slate-50"
          >
            {t("ops.promote.reset_btn")}
          </button>
          {loading && <span className="text-[11px] text-slate-500">{t("ops.common.in_flight_ellipsis")}</span>}
        </div>
      </div>

      <div className="flex flex-col gap-3">
        <DryRunDiffPanel dryRun={dryRun} stale={stale} dataTestId="rl-ops-rollback-dry-run-result" />
        <ApplyConfirmStrip
          disabled={applyDisabled}
          disabledReason={disabledReason}
          onApply={onApply}
          confirmPhrase="APPLY"
          applyLabel={t("ops.promote.rollback_apply")}
          dataTestId="rl-ops-rollback-apply-strip"
        />
        {error && (
          <div data-testid="rl-ops-rollback-error" className="rounded-md border border-rose-200 bg-rose-50 p-3 text-xs text-rose-800">
            {t("ops.common.error_prefix")} {error.status || ""}: {error.detail || t("ops.common.error_unknown")}
          </div>
        )}
        {applyState && (
          <div data-testid="rl-ops-rollback-apply-result" className="rounded-md border border-emerald-200 bg-emerald-50 p-3">
            <p className="mb-2 text-xs font-medium text-emerald-900">
              {t("ops.rollback.rolled_back_line", { action: applyState.action, at: applyState.at })}
            </p>
            <JSONInspector value={applyState} dataTestId="rl-ops-rollback-apply-json" />
          </div>
        )}
      </div>
    </section>
  );
};

export default RLAdminOpsPromote;
